// Billing model registry.
//
// Every client billing model lives here: how it is labeled, which config
// fields it exposes, and how it turns hours into an amount owed. Adding a
// model means adding an entry here, not hunting ternaries across Dashboard.js.
//
// `config` is the shape stored in client_billing_terms.billing_config. The
// legacy clients.* columns (rate / retainer / hours / overage) use the same
// keys, so a client row can be passed directly as a config during the window
// where clients.* is still the compatibility cache. See the migration
// 20260819_billing_terms_foundation.sql for what is authoritative.
//
// LABELS: one name per model, used on every screen (Page System decision 3,
// 2026-09-25): 'Hourly', 'Retainer', 'Retainer + overage'. Until then the UI
// used four spellings per model ('Hourly T&M', 'Fixed', 'Fixed Retainer',
// 'Hybrid', 'Hybrid Retainer'...); "Hybrid" in particular meant nothing to a
// new user. The tableLabel / shortLabel / selectLabel keys are kept as aliases
// of `label` so existing callers keep working. The model ids ('hybrid' etc.)
// are unchanged -- this is display only.

// Matches the `|| 0` coercion the original calcOwed used, but also survives
// numerics arriving as strings from PostgREST.
const num = (v) => Number(v) || 0;

/**
 * Money on a CONTRACT: grouped thousands, cents only when there are any.
 *
 * Deliberately not the dashboard's fmtMoney, which rounds to whole dollars --
 * right for a revenue tile, wrong for a rate somebody signs. It mirrors
 * fmtRate's rule ($42.50/hr, $1,818/hr) and is mirrored again in
 * supabase/functions/_shared/optionTerms.ts so the PDF reads the same as the
 * screen; scripts/test-option-terms.mjs holds the two together.
 *
 * Spelled out rather than toLocaleString, which gave "$1,250.5" for a rate of
 * 1250.50 -- a price with one decimal place, on an agreement.
 */
export function money(v, symbol = '$') {
  const n = num(v);
  const whole = Math.trunc(Math.abs(n));
  const cents = Math.round((Math.abs(n) - whole) * 100);
  const grouped = String(whole).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const body = cents ? `${grouped}.${String(cents).padStart(2, '0')}` : grouped;
  return `${n < 0 ? '-' : ''}${symbol}${body}`;
}

export const BILLING_MODELS = {
  hourly: {
    id: 'hourly',
    label: 'Hourly',
    tableLabel: 'Hourly',
    shortLabel: 'Hourly',
    selectLabel: 'Hourly',
    badge: 'blue',
    isFixedMonthly: false,
    hasOverage: false,
    fields: [
      { key: 'rate', label: 'Hourly Rate ($)',
        tip: 'Amount billed per hour of logged time for this client.' },
    ],
    calc: (cfg, hrs) => hrs * num(cfg.rate),
    summary: (cfg, fmt = money) => `${fmt(num(cfg.rate))}/hr`,
  },

  retainer: {
    id: 'retainer',
    label: 'Retainer',
    tableLabel: 'Retainer',
    shortLabel: 'Retainer',
    selectLabel: 'Retainer',
    badge: 'green',
    isFixedMonthly: true,
    hasOverage: false,
    fields: [
      { key: 'retainer', label: 'Monthly Fee ($)',
        tip: 'Fixed amount billed each month regardless of hours worked.' },
    ],
    calc: (cfg) => num(cfg.retainer),
    summary: (cfg, fmt = money) => `${fmt(num(cfg.retainer))}/mo`,
  },

  hybrid: {
    id: 'hybrid',
    label: 'Retainer + overage',
    tableLabel: 'Retainer + overage',
    shortLabel: 'Retainer + overage',
    selectLabel: 'Retainer + overage',
    badge: 'amber',
    isFixedMonthly: true,
    hasOverage: true,
    fields: [
      { key: 'retainer', label: 'Monthly Base Fee ($)',
        tip: 'The flat retainer portion billed each month. Hours up to the included limit are covered by this fee.' },
      { key: 'hours', label: 'Included Hours',
        tip: 'Hours covered by the monthly base fee. Any hours beyond this are billed at the overage rate.' },
      { key: 'overage', label: 'Overage Rate ($/hr)',
        tip: 'Rate charged for hours beyond the included retainer hours. Formula: retainer + (hours over limit × overage rate).' },
    ],
    calc: (cfg, hrs) =>
      num(cfg.retainer) + Math.max(0, hrs - num(cfg.hours)) * num(cfg.overage),
    summary: (cfg, fmt = money) =>
      `${fmt(num(cfg.retainer))}/mo for ${num(cfg.hours)}h, then ${fmt(num(cfg.overage))}/hr`,
  },
};

// Order the client editor's dropdown presents. Preserves today's order,
// which is not alphabetical and not the object key order.
export const MODEL_ORDER = ['retainer', 'hybrid', 'hourly'];

export const MODEL_IDS = Object.keys(BILLING_MODELS);

export const isKnownModel = (id) => Object.prototype.hasOwnProperty.call(BILLING_MODELS, id);

// Returns null for an unrecognized id. Callers must handle null rather than
// silently falling back to a model -- guessing here would mean billing a
// client on terms nobody chose.
export const getModel = (id) => (isKnownModel(id) ? BILLING_MODELS[id] : null);

// The billing_config for a model, pulled off either a client row (legacy
// cache) or a client_billing_terms.billing_config object.
export function configFromClient(client) {
  if (!client) return {};
  return {
    rate: client.rate,
    retainer: client.retainer,
    hours: client.hours,
    overage: client.overage,
  };
}

// Drop-in replacement for the original Dashboard.calcOwed(client, hrs).
// Unknown or missing model returns 0, exactly as before.
export function calcOwed(client, hrs) {
  if (!client) return 0;
  const m = getModel(client.model);
  if (!m) return 0;
  return m.calc(configFromClient(client), hrs);
}

// Same calculation against explicit terms rather than a client row. This is
// the form invoice generation should move to, since it can be handed a
// historical terms row instead of today's cache.
export function calcOwedFromTerms(terms, hrs) {
  if (!terms) return 0;
  const m = getModel(terms.billing_model_id);
  if (!m) return 0;
  return m.calc(terms.billing_config || {}, hrs);
}

// Hours beyond the included allowance. Zero for models without overage.
export function overageHours(client, hrs) {
  const m = getModel(client?.model);
  if (!m || !m.hasOverage) return 0;
  return Math.max(0, hrs - num(client.hours));
}

// A label helper so call sites read as intent rather than as a ternary.
// `variant` is one of the four documented above.
export function modelLabel(id, variant = 'label') {
  const m = getModel(id);
  if (!m) return id || '';
  return m[variant] || m.label;
}

export function modelBadge(id) {
  return getModel(id)?.badge || 'blue';
}

/**
 * Has anyone actually entered a price for this model?
 *
 * An option can carry a model with none of its figures filled in -- a real
 * state, reached by choosing "Hybrid" and saving before typing anything. The
 * summary of that is "$0/mo for 0h, then $0/hr", which is fine as arithmetic
 * and unacceptable on a contract: it states terms nobody agreed to.
 *
 * So the emptiness decision lives here, once, and reads the model's OWN field
 * list -- a model added to this registry is covered with no change. An
 * explicit zero counts as a price; only absence does not. Mirrored, per model,
 * in supabase/functions/_shared/optionTerms.ts and pinned by
 * scripts/test-option-terms.mjs.
 */
export function hasPriceFields(modelId, config) {
  const m = BILLING_MODELS[modelId];
  if (!m) return false;
  const cfg = config || {};
  return (m.fields || []).some((f) => {
    const v = cfg[f.key];
    return v !== null && v !== undefined && String(v).trim() !== '' && Number.isFinite(Number(v));
  });
}
